import { addYears } from '../model/addYears'
import type { IData } from '../model/IData'
import { type IDateFallback } from '../model/IDateFallback'
import type { IPerson } from '../model/IPerson'
import { MAX_AGE } from '../model/MAX_AGE'
import { expectDefined } from './expectDefined'
import { formatDate } from './formatDate'
import { getAverageFirstMarriageAge } from './getAverageFirstMarriageAge'
import { getChildren } from './getChildren'
import { getCouples } from './getCouples'
import { getParentCouple } from './getParentCouple'
import { getPersonName } from './getPersonName'
import { getSpouses } from './getSpouses'

const results = new Map<string, IDateFallback>()

export function getBirthDateFallback(
	data: IData,
	person: IPerson,
): Readonly<IDateFallback> {
	// Check cache.
	const lastResult = results.get(person.id)
	if (lastResult) {
		return lastResult
	}
	// Get default result.
	let result: IDateFallback = {
		value: person.birthDate,
		isFallback: !person.birthDate,
	}
	if (result.value == null) {
		// Assume birth after parents marriage, adding a year after any older
		// siblings.
		const parentCouple = getParentCouple(data, person.id)
		if (parentCouple) {
			const childIndex = parentCouple.childIds!.indexOf(person.id)
			let baseYear = parentCouple.marriedDate
			let extraYears = 1 // Assume a year after marriage.
			let fallbackSource =
				'A szülők házasságának dátumától egy évre, idősebb testvérenként is egy-egy évet számolva.'
			if (childIndex) {
				// Try to find an older sibling with a birth date and offset by a year
				// per older siblings. This runs regardless of base year already found,
				// because this looks for a new base year.
				for (let i = 0; i < childIndex; i++) {
					const olderSiblingId = parentCouple.childIds![i]!
					const olderSibling = expectDefined(data.personsById[olderSiblingId])
					if (olderSibling.birthDate) {
						baseYear = olderSibling.birthDate
						extraYears = 1 // Assume a year after this older sibling.
						fallbackSource = `Idősebb testvér (${getPersonName(olderSibling)}) születésétől számított egy évvel később, esetleges további idősebb testvérenként is egy-egy évet számolva.`
					} else {
						extraYears++
					}
				}
			}
			if (baseYear == null) {
				// No parent marriage date, no sibling birth dates.

				// Get the latest parent birth date and offset it by the average
				// marriage age.
				const father = parentCouple.maleId
					? data.personsById[parentCouple.maleId]
					: undefined
				const mother = parentCouple.femaleId
					? data.personsById[parentCouple.femaleId]
					: undefined
				let isMale = false
				if (father?.birthDate && mother?.birthDate) {
					if (father.birthDate < mother.birthDate) {
						baseYear = father.birthDate
						isMale = true
					} else {
						baseYear = mother.birthDate
					}
				} else if (father?.birthDate) {
					baseYear = father.birthDate
					isMale = true
				} else if (mother?.birthDate) {
					baseYear = mother.birthDate
				}
				extraYears = getAverageFirstMarriageAge(data, isMale) + 1 + childIndex
				fallbackSource = `Az ${isMale ? 'apa' : 'anya'} születésétől számítva az átlagos első házasságkötési életkor, plusz egy év.`
			}
			if (baseYear) {
				result.value = addYears(baseYear, extraYears)
				result.fallbackSource = fallbackSource
			}
		}
	}

	const averageBirthYearDifference =
		getAverageFirstMarriageAge(data, true) -
		getAverageFirstMarriageAge(data, false)

	if (result.value == null) {
		const couples = getCouples(data, person.id).toSorted((a, b) =>
			(a.marriedDate ?? '9999').localeCompare(b.marriedDate ?? '9999'),
		)
		const hasUnknownMarriageDate = couples.find((it) => it.marriedDate == null)
		if (!hasUnknownMarriageDate) {
			// Some marriages have unknown date. Assume age difference from oldest spouse.
			const oldestSpouse = getSpouses(data, person.id)
				.filter((it) => !!it.birthDate)
				.sort((a, b) => a.birthDate!.localeCompare(b.birthDate!))
				.at(0)
			if (oldestSpouse) {
				result.value = addYears(
					oldestSpouse.birthDate!,
					person.isMale
						? -averageBirthYearDifference
						: averageBirthYearDifference,
				)
				result.fallbackSource = `A legidősebb házastárs (${getPersonName(oldestSpouse)}) életkorából, az átlagos első házasságkötési korkülönbséggel számolva.`
			}
		}

		if (result.value == null) {
			// Assume average age at the earliest marriage.
			const earliestCouple = couples[0]
			if (earliestCouple) {
				const earliestSpouse =
					data.personsById[
						(person.isMale ? earliestCouple.femaleId : earliestCouple.maleId) ??
							''
					]
				if (earliestCouple.marriedDate) {
					result.value = addYears(
						earliestCouple.marriedDate!,
						-getAverageFirstMarriageAge(data, person.isMale),
					)
					result.fallbackSource = `A${couples.length === 1 ? '' : ' legkorábbi'} házassági dátumból (${formatDate(earliestCouple.marriedDate)}) az átlagos első házasságkötési életkorral számolva.`
				} else if (earliestSpouse?.birthDate) {
					result.value = addYears(
						earliestSpouse.birthDate,
						person.isMale
							? -averageBirthYearDifference
							: averageBirthYearDifference,
					)
					result.fallbackSource = `A${couples.length === 1 ? '' : ' legkorábbi házasságából ismert'} házastársa (${getPersonName(earliestSpouse)}) születési évéből, az átlagos első házasságkötési korkülönbséggel számolva.`
				}
			}
		}
	}
	if (result.value == null) {
		// Assume average age at the earliest childbirth.
		const oldestChild = getChildren(data, person.id)
			.filter((it) => it.birthDate != null)
			.at(0)
		const averageMarriageYears = getAverageFirstMarriageAge(data, person.isMale)

		if (oldestChild && oldestChild.birthDate) {
			result.value = addYears(oldestChild.birthDate, -averageMarriageYears - 1)
			result.fallbackSource = `A legidősebb gyermek (${getPersonName(oldestChild)}) születési évéből a házasság idejére következtetve, majd abból az átlagos első házasságkötési életkorral számolva.`
		}
	}
	if (result.value == null && person.deathDate) {
		// Assume average age at death and count backwards.
		result.value = addYears(person.deathDate, -MAX_AGE / 2)
		result.fallbackSource = `A halál időpontjától az legnagyobb ismert életkor felével (${MAX_AGE / 2} év) visszaszámolva.`
	}
	if (result.value == null) {
		// Assume average age at the earliest assumed childbirth.
		const assumedOldestChild = getChildren(data, person.id)
			.map((it) => ({ child: it, birthDate: getBirthDateFallback(data, it) }))
			.filter((it) => it.birthDate.value != null)
			.sort()
			.at(0)
		if (assumedOldestChild?.birthDate.value) {
			const averageMarriageYears = getAverageFirstMarriageAge(
				data,
				person.isMale,
			)
			result.value = addYears(
				assumedOldestChild.birthDate.value,
				-averageMarriageYears - 1,
			)
			result.fallbackSource = `A legidősebbnek becsült gyermek (${getPersonName(assumedOldestChild.child)}) születési évéből a házasság idejére következtetve, majd abból az átlagos első házasságkötési életkorral számolva.`
		}
	}
	if (result.isFallback && result.value) {
		result.value = result.value.slice(0, 4)
	}
	results.set(person.id, result)
	return result
}
